import type { Participant } from '@seen/core/models/patients/participant'
import { Button, formatSsn, Label, MaskedInput, maskSsnForView } from '@seen/ui'
import { IconEye, IconEyeOff, IconPencil } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function SsnField({
  participant,
}: {
  participant: Participant
}): JSX.Element {
  const [isEditing, setIsEditing] = useState(false)
  const [isRevealed, setIsRevealed] = useState(false)
  const isReadOnly = useIsReadOnly()

  if (isEditing) {
    return (
      <div className="grid">
        <Label>SSN</Label>
        <ResourceFieldForm
          instance={participant}
          valueFn={(p) => p.ssn}
          patchFn={(p, value) => {
            p.ssn = value ?? undefined
          }}
          debounceTyping
          render={({ field: { onChange, onBlur, value, disabled } }) => (
            <MaskedInput
              className="h-auto rounded-none border-0 border-b px-0 py-2 ring-offset-0 hover:border-zinc-400 focus-visible:border-zinc-950 focus-visible:ring-0 dark:focus-visible:border-zinc-300"
              mask="000-00-0000"
              unmask
              overwrite
              autoFocus
              disabled={disabled}
              placeholder="000-00-0000"
              value={value ?? ''}
              onAccept={(v) => {
                onChange(v || null)
              }}
              onBlur={() => {
                onBlur()
                setIsEditing(false)
                setIsRevealed(false)
              }}
            />
          )}
        />
      </div>
    )
  }

  const ssn = participant.ssn
  const displayValue = ssn
    ? isRevealed
      ? formatSsn(ssn)
      : maskSsnForView(ssn)
    : undefined

  return (
    <div className="grid">
      <Label>SSN</Label>
      <div className="group flex items-center gap-1">
        <span className="flex-1 text-muted-foreground">{displayValue}</span>
        {!!ssn && (
          <div className="invisible flex group-hover:visible">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setIsRevealed((prev) => !prev)
              }}
            >
              {isRevealed ? (
                <IconEyeOff className="size-4" />
              ) : (
                <IconEye className="size-4" />
              )}
            </Button>
          </div>
        )}
        {!isReadOnly && (
          <div className="invisible flex group-hover:visible">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setIsEditing(true)
              }}
            >
              <IconPencil className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
